/**
 * Contrato de telemetría normalizada.
 *
 * @ported-from GuardIA1224/guardia-platform-sites@776c789 src/integrations/gpswox/types.ts
 *
 * Un GuardiaHistoryRecord es un punto GPS de WOX ya normalizado y clasificado.
 * Es la única forma en que la telemetría entra al motor: el motor no sabe que
 * existe GPSWox, no conoce su formato crudo y no habla con él.
 */

export type ValidationStatus = "VALID" | "DISCARDED" | "INCONCLUSIVE";

/**
 * Motivo de validación de un punto que el proveedor marcó como posición
 * inválida (en WOX, `valid: 0`). El motor lo traduce a la bandera
 * `provider_marked_invalid`, y el punto no abre ni cierra tramo (ADR-010).
 */
export const PROVIDER_MARKED_INVALID = "PROVIDER_MARKED_INVALID" as const;

export type GuardiaDevice = {
  device_id: string;
  name: string | null;
  plate: string | null;
  imei: string | null;
  active: boolean | null;
  online: boolean | null;
  protocol: string | null;
  group_id: string | null;
  group: string | null;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  total_distance: number | null;
};

/**
 * Igual que GuardiaDevice pero con los IDs de usuario de WOX autorizados sobre
 * el dispositivo. Este tipo NUNCA sale del servidor: la lista de usuarios de
 * WOX no llega al navegador (constitución, Art. 4).
 */
export type GuardiaDeviceInternal = GuardiaDevice & { authorizedUserIds: number[] };

export type GuardiaHistoryRecord = {
  source_record_id: string;
  device_id: string;
  plate: string | null;
  timestamp: string | null;
  server_timestamp: string | null;
  latitude: number | null;
  longitude: number | null;
  altitude_m: number | null;
  speed_kmh: number | null;
  heading: number | null;
  distance: number | null;
  total_distance: number | null;
  ignition: boolean | null;
  motion: boolean | null;
  satellites: number | null;
  battery: number | null;
  alarm: string | null;
  event_type: string | null;
  protocol: string | null;
  source: "gpswox";
  source_schema_version: "gpswox-history-v1";
  normalizer_version: "gpswox-normalizer-v0.1";
  /**
   * Instante de ingesta. DIVERGENCIA-01 frente a V5: allá se rellenaba con
   * `new Date()` dentro del normalizador; aquí se inyecta. El motor no lee
   * este campo — pero con un reloj adentro el registro no se puede hashear de
   * forma reproducible, y el Artículo 10 exige una firma que se pueda repetir.
   */
  ingested_at: string;
  /** `null` cuando el proveedor dio identificador: ahí no hace falta hashear. */
  raw_record_hash: string | null;
  raw_attributes: Record<string, unknown>;
  validation_status: ValidationStatus;
  validation_reason: string | null;
  validator_version: "guardia-validator-v0.1";
};

/** Campos que NO entran en el digest de entrada, por no ser parte del dato. */
export const DIGEST_EXCLUDED_FIELDS = ["ingested_at"] as const;
